import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const BleedingPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', troubleshooting: 'トラブル対策', title: '滲み・オーバーハング' },
    badge: '中程度',
    title: '滲み・オーバーハング',
    description: 'サポートなし部分の垂れ下がりや、表面の滲み。冷却と温度のバランスが重要。',
    symptoms: {
      title: '症状の例',
      items: [
        'オーバーハング部分が垂れ下がる',
        'ブリッジ部分が波打つ',
        '表面に樹脂が滲み出る',
        '下面の仕上がりが粗い'
      ]
    },
    causes: {
      title: '主な原因',
      items: [
        { title: '1. ノズル温度過高', desc: '温度が高すぎると樹脂の粘度が下がり、重力で垂れやすくなります。' },
        { title: '2. 冷却不足', desc: 'パートクーリングファンが弱いと、樹脂が固まる前に垂れてしまいます。' },
        { title: '3. 印刷速度過速', desc: 'オーバーハング部分の速度が速すぎると、樹脂が安定する前に次の層に移ってしまいます。' },
        { title: '4. サポート設定不適切', desc: 'サポートが必要な角度なのに設定されていない、またはサポート密度が不足している場合に発生します。' }
      ]
    },
    solutions: {
      title: '対策・チェックポイント',
      items: [
        {
          title: 'ノズル温度の調整',
          desc: '推奨温度範囲の下限に近づけます。',
          points: ['5°C刻みで下げてテスト', '層間接着が弱くならない範囲で調整', 'オーバーハング部分のみ温度を下げる設定も可能']
        },
        {
          title: 'ファン速度の強化',
          desc: '冷却を強化して樹脂を素早く固めます。',
          points: ['オーバーハング部分でファン100%', 'ブリッジ設定でファン最大化', 'ABS/ASAでは慎重に（層間剥離に注意）']
        },
        {
          title: '印刷速度の調整',
          desc: 'オーバーハング部分の速度を落とします。',
          points: ['オーバーハング速度: 20-30mm/s', 'ブリッジ速度: 25-35mm/s', '通常速度の50-70%程度']
        },
        {
          title: 'サポート設定の最適化',
          desc: '適切なサポートで垂れ下がりを防ぎます。',
          points: ['サポート角度: 45-50度以上', 'サポート密度: 15-20%', 'ツリーサポートの活用', 'サポート接触距離の調整']
        },
        {
          title: 'モデルの向きの変更',
          desc: '可能であれば、オーバーハングが少なくなる向きに配置します。',
          points: ['45度以上のオーバーハングを避ける', 'サポートが少なくなる向きを検討', '強度と仕上がりのバランスを考慮']
        }
      ]
    },
    overhangGuide: {
      title: 'オーバーハング角度の目安',
      items: [
        { angle: '0-45度', desc: 'サポート不要、良好な仕上がり', color: '[#5f8d63]' },
        { angle: '45-60度', desc: '冷却強化で対応可能', color: '[#4c7ca6]' },
        { angle: '60-75度', desc: 'サポート推奨', color: '[#b56b3c]' },
        { angle: '75度以上', desc: 'サポート必須', color: '[#8b4545]' }
      ]
    },
    related: {
      title: '関連情報',
      items: [
        { title: 'PLA フィラメント', desc: 'オーバーハングに強い材料', link: '/filament/pla' },
        { title: '糸引き（ストリンギング）', desc: '関連するトラブル', link: '/troubleshooting/stringing' }
      ]
    },
    backLink: 'トラブル対策一覧に戻る'
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', troubleshooting: 'Troubleshooting', title: 'Sagging & Overhangs' },
    badge: 'Moderate',
    title: 'Sagging & Overhangs',
    description: 'Drooping of unsupported areas and surface sagging. Balance of cooling and temperature is key.',
    symptoms: {
      title: 'Symptoms',
      items: [
        'Overhang areas drooping',
        'Bridge sections sagging',
        'Filament oozing on surface',
        'Rough finish on bottom surfaces'
      ]
    },
    causes: {
      title: 'Main Causes',
      items: [
        { title: '1. Nozzle Temperature Too High', desc: 'Excessive temperature reduces filament viscosity, making it prone to drooping under gravity.' },
        { title: '2. Insufficient Cooling', desc: 'Weak part cooling allows filament to droop before solidifying.' },
        { title: '3. Print Speed Too Fast', desc: 'Excessive speed on overhangs causes filament to move to next layer before stabilizing.' },
        { title: '4. Improper Support Settings', desc: 'Missing supports for angles that need them, or insufficient support density.' }
      ]
    },
    solutions: {
      title: 'Solutions & Checkpoints',
      items: [
        {
          title: 'Adjust Nozzle Temperature',
          desc: 'Approach lower end of recommended temperature range.',
          points: ['Decrease in 5°C increments and test', 'Adjust within range that maintains layer adhesion', 'Can lower temperature for overhang areas only']
        },
        {
          title: 'Increase Fan Speed',
          desc: 'Enhance cooling to solidify filament quickly.',
          points: ['100% fan for overhang areas', 'Maximize fan for bridge settings', 'Be cautious with ABS/ASA (risk of delamination)']
        },
        {
          title: 'Adjust Print Speed',
          desc: 'Slow down for overhang areas.',
          points: ['Overhang speed: 20-30mm/s', 'Bridge speed: 25-35mm/s', 'About 50-70% of normal speed']
        },
        {
          title: 'Optimize Support Settings',
          desc: 'Prevent drooping with appropriate supports.',
          points: ['Support angle: 45-50 degrees and above', 'Support density: 15-20%', 'Use tree supports', 'Adjust support contact distance']
        },
        {
          title: 'Change Model Orientation',
          desc: 'If possible, orient to minimize overhangs.',
          points: ['Avoid overhangs above 45 degrees', 'Consider orientation with fewer supports', 'Balance strength and finish quality']
        }
      ]
    },
    overhangGuide: {
      title: 'Overhang Angle Guidelines',
      items: [
        { angle: '0-45°', desc: 'No support needed, good finish', color: '[#5f8d63]' },
        { angle: '45-60°', desc: 'Manageable with enhanced cooling', color: '[#4c7ca6]' },
        { angle: '60-75°', desc: 'Support recommended', color: '[#b56b3c]' },
        { angle: '75°+', desc: 'Support required', color: '[#8b4545]' }
      ]
    },
    related: {
      title: 'Related Information',
      items: [
        { title: 'PLA Filament', desc: 'Material good for overhangs', link: '/filament/pla' },
        { title: 'Stringing', desc: 'Related issue', link: '/troubleshooting/stringing' }
      ]
    },
    backLink: 'Back to Troubleshooting List'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.troubleshooting, path: `${prefix}/troubleshooting` },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="tech-label bg-[#4c7ca6] text-white">{TEXT.badge}</span>
                <h1 className="text-4xl font-bold text-white font-['Exo']">
                  {TEXT.title}
                </h1>
              </div>
              <p className="text-metal-light text-lg">
                {TEXT.description}
              </p>
              <div className="section-divider"></div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.symptoms.title}
              </h2>
              <ul className="space-y-3 text-metal-light">
                {TEXT.symptoms.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-arrow-right-s-line text-accent mt-1"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.causes.title}
              </h2>
              <div className="space-y-4">
                {TEXT.causes.items.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-l-[#4c7ca6] pl-4">
                    <h3 className="text-lg font-semibold text-accent font-['Exo'] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.solutions.title}
              </h2>
              <div className="space-y-4">
                {TEXT.solutions.items.map((item, idx) => (
                  <div key={idx} className="clickable-card p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] text-xl mt-1"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Exo'] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-metal-light text-sm mb-2">
                          {item.desc}
                        </p>
                        <ul className="text-metal-light text-sm space-y-1 ml-4">
                          {item.points.map((point, pidx) => (
                            <li key={pidx}>• {point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.overhangGuide.title}
              </h2>
              <div className="space-y-3 text-metal-light">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {TEXT.overhangGuide.items.map((item, idx) => (
                    <div key={idx} className={`border-l-4 border-l-${item.color} pl-4`}>
                      <h3 className="text-accent font-semibold mb-1">{item.angle}</h3>
                      <p className="text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.related.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEXT.related.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={`${prefix}${item.link}`}
                    className="clickable-card p-4 hover:border-l-4 hover:border-l-accent transition-all"
                  >
                    <h3 className="text-white font-['Exo'] font-semibold mb-1">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to={`${prefix}/troubleshooting`}
                className="flex items-center space-x-2 text-accent hover:text-white transition-colors font-['Roboto_Mono']"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{TEXT.backLink}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BleedingPage;
